import React, { useState } from 'react'
import { Menu, MenuItem, Typography } from '@mui/material'
import { LabraToolTipV2 } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPenToSquare,
  faXmark,
  faClockRotateLeft,
  faExpand,
  faWindowRestore,
  faSidebarFlip,
} from '@fortawesome/pro-regular-svg-icons'
import { LabraChatLayout } from '../../../modules/labraChat/reducer'
import { useStyles } from './ChatHeaderControls.styles'

const LAYOUTS: {
  layout: LabraChatLayout
  label: string
  icon: typeof faExpand
}[] = [
  { layout: 'floating', label: 'Floating', icon: faWindowRestore },
  { layout: 'sidebar', label: 'Sidebar', icon: faSidebarFlip },
  { layout: 'expanded', label: 'Fully expanded', icon: faExpand },
]

interface ChatHeaderControlsProps {
  isHistoryOpen: boolean
  onNewChat: () => void
  onToggleHistory: () => void
  onClose: () => void
  /** When true, the layout control is omitted. */
  hideFullScreenToggle?: boolean
  layout: LabraChatLayout
  /** Sidebar is offered only where the host gives the chat a column. */
  canDock: boolean
  onLayoutChange: (layout: LabraChatLayout) => void
}

const ChatHeaderControlsComponent: React.FC<ChatHeaderControlsProps> = ({
  isHistoryOpen,
  onNewChat,
  onToggleHistory,
  layout,
  canDock,
  onLayoutChange,
  onClose,
  hideFullScreenToggle = false,
}) => {
  const { classes, cx } = useStyles()
  const [layoutAnchorEl, setLayoutAnchorEl] = useState<HTMLElement | null>(null)

  const options = canDock
    ? LAYOUTS
    : LAYOUTS.filter(option => option.layout !== 'sidebar')
  const current = options.find(option => option.layout === layout) ?? options[0]

  return (
    <div className={classes.controls}>
      <div className={classes.iconGroup}>
        <LabraToolTipV2
          tooltipPlacement="bottom"
          description={isHistoryOpen ? 'Back to chat' : 'Chat history'}
        >
          <button
            className={cx(classes.iconButton, {
              [classes.iconButtonActive]: isHistoryOpen,
            })}
            type="button"
            onClick={onToggleHistory}
            aria-label={isHistoryOpen ? 'Back to chat' : 'Chat history'}
            aria-pressed={isHistoryOpen}
          >
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </button>
        </LabraToolTipV2>
        <LabraToolTipV2 tooltipPlacement="bottom" description="New chat">
          <button
            className={classes.iconButton}
            type="button"
            onClick={onNewChat}
            aria-label="New chat"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
        </LabraToolTipV2>
        {!hideFullScreenToggle && (
          <>
            <LabraToolTipV2 tooltipPlacement="bottom" description="Layout">
              <button
                className={classes.iconButton}
                type="button"
                onClick={event => setLayoutAnchorEl(event.currentTarget)}
                aria-label="Layout"
                aria-haspopup="menu"
                aria-expanded={!!layoutAnchorEl}
              >
                <FontAwesomeIcon icon={current.icon} />
              </button>
            </LabraToolTipV2>
            <Menu
              anchorEl={layoutAnchorEl}
              open={!!layoutAnchorEl}
              onClose={() => setLayoutAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: { className: classes.layoutPaper, elevation: 0 },
                list: { className: classes.layoutList },
              }}
            >
              <Typography className={classes.layoutHeading}>LAYOUT</Typography>
              {options.map(option => (
                <MenuItem
                  key={option.layout}
                  className={cx(classes.layoutItem, {
                    [classes.layoutItemActive]: option.layout === layout,
                  })}
                  onClick={() => {
                    setLayoutAnchorEl(null)
                    onLayoutChange(option.layout)
                  }}
                >
                  <FontAwesomeIcon icon={option.icon} />
                  <Typography className={classes.layoutLabel}>
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </div>
      <div className={classes.separator} />
      <LabraToolTipV2 tooltipPlacement="bottom" description="Close chat">
        <button
          className={classes.closeButton}
          type="button"
          onClick={onClose}
          aria-label="Close chat"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </LabraToolTipV2>
    </div>
  )
}

export const ChatHeaderControls = React.memo(ChatHeaderControlsComponent)
